import { Image, ItemIdentifier } from '@conveyr/shared-types';
import type { operations } from '@/api/generated/openapi';

export type GetOrderPath = operations['get-order']['parameters']['path'];
export type GetOrderResponse = operations['get-order']['responses']['200']['content']['application/json'];
export type DetailedOrderRaw = GetOrderResponse['order'];
export type DetailedOrderChannelRaw = DetailedOrderRaw['channel'];
export type DetailedOrderCustomerRaw = NonNullable<DetailedOrderRaw['customer']>;
export type DetailedOrderShippingAddressRaw = NonNullable<DetailedOrderRaw['shipping_address']>;
export type DetailedOrderItemRaw = DetailedOrderRaw['order_items'][number];
export type DetailedOrderInventoryItemRaw = DetailedOrderItemRaw['item'];
export type DetailedOrderItemIdentifierRaw = DetailedOrderInventoryItemRaw['identifiers'][number];
export type DetailedOrderItemImageRaw = DetailedOrderInventoryItemRaw['images'][number];
export type DetailedOrderPickDataRaw = DetailedOrderItemRaw['pick_data'];
export type DetailedOrderPickScanRaw = NonNullable<Exclude<DetailedOrderPickDataRaw, unknown[] | null>['scans']>[number];
export type DetailedOrderPackageRaw = DetailedOrderRaw['packages'][number];
export type DetailedOrderPackageBoxRaw = NonNullable<DetailedOrderPackageRaw['box']>;
export type DetailedOrderPackageItemRaw = DetailedOrderPackageRaw['package_items'][number];
export type DetailedOrderAttachmentRaw = DetailedOrderRaw['attachments'][number];
export type DetailedOrderTrackingNumberRaw = DetailedOrderRaw['tracking_numbers'][number];
export type DetailedOrderQuotesRaw = NonNullable<DetailedOrderRaw['quotes']>;
export type DetailedOrderQuoteRaw = DetailedOrderQuotesRaw['quotes'][number];
export type DetailedOrderQuoteItemRaw = DetailedOrderQuoteRaw['quote_items'][number];
export type DetailedOrderMessageRaw = NonNullable<DetailedOrderRaw['errors']>[number];
export type DetailedOrderFulfillmentRequestRaw = GetOrderResponse['fulfillment_requests'][number];
export type DetailedOrderFulfillmentRaw = DetailedOrderFulfillmentRequestRaw['fulfillments'][number];

export interface DetailedOrderChannel {
    id: number
    createdAt: string
    updatedAt: string
    type: DetailedOrderChannelRaw['type']
    nickname: string | null
    channelAccountId: string | null
    connectionStatus: string | null
    merchantId: number
    uuid: string
    merchantUuid: string
    deletedAt: string | null
    teamId: number
}

export interface DetailedOrderCustomer {
    id: number
    createdAt: string
    updatedAt: string
    name: string | null
    channelId: number | null
    defaultAddressId: number | null
    email: string | null
    phoneNumber: string | null
    emailHash: string | null
    nameAddressHash: string | null
    merchantId: number
    teamId: number
}

export interface DetailedOrderShippingAddress {
    id: number
    createdAt: string
    updatedAt: string
    customerId: number
    addressLine1: string
    addressLine2: string | null
    addressLine3: string | null
    city: string
    stateProvince: string | null
    countryCode: string
    postalCode: string
    phoneNumber: string | null
    isResidential: boolean
    name: string | null
    latitude: string | null
    longitude: string | null
}

export interface DetailedOrderInventoryItem {
    id: number
    createdAt: string | null
    updatedAt: string | null
    merchantSku: string
    title: string
    condition: DetailedOrderInventoryItemRaw['condition']
    conditionNote: string | null
    lengthMm: number | null
    widthMm: number | null
    heightMm: number | null
    weightGm: number | null
    uuid: string
    merchantId: number
    deletedAt: string | null
    areDimensionsLocked: boolean | null
    teamId: number
    searchableIdentifiers: string
    identifiers: ItemIdentifier[]
    images: Image[]
}

export type DetailedOrderPickScan
    = | { type: DetailedOrderPickScanRaw['type'], code: string, scannedByUserId: number, scannedAt: string }
        | { type: DetailedOrderPickScanRaw['type'], skippedByUserId: number, skippedAt: string };

export interface DetailedOrderPickData {
    scans?: DetailedOrderPickScan[]
    quantityPicked?: number
    toteCode?: string | null
}

export interface DetailedOrderItem {
    id: number
    createdAt: string | null
    updatedAt: string | null
    orderId: number
    itemId: number
    listingId: number | null
    quantity: number
    pickedAt: string | null
    // Pick data stored empty comes back as [], distinct from an object and from null.
    pickData: DetailedOrderPickData | [] | null
    externalId: string | null
    shipmentLineUuid: string
    item: DetailedOrderInventoryItem
}

export interface DetailedOrderAttachment {
    id: number
    createdAt: string
    updatedAt: string
    path: string
    name: string
    url: string
    attachableId: number
    attachableType: DetailedOrderAttachmentRaw['attachable_type']
}

export interface DetailedOrderPackageBox {
    id: number
    packageId: number
    weightGm: number
    lengthMm: number
    widthMm: number
    heightMm: number
    createdAt: string
    updatedAt: string
}

export interface DetailedOrderPackageItem {
    id: number
    createdAt: string | null
    updatedAt: string | null
    orderItemId: number
    packageId: number
    quantity: number
    toteBarcode: string | null
    packedAt: string | null
    packedByUserId: number | null
    lotCode: string | null
}

export interface DetailedOrderPackage {
    id: number
    createdAt: string | null
    updatedAt: string | null
    orderId: number
    shippedAt: string | null
    arrivedAt: string | null
    lpn: string | null
    hash: string
    fulfillmentRequestId: string | null
    box: DetailedOrderPackageBox | null
    attachments: DetailedOrderAttachment[]
    packageItems: DetailedOrderPackageItem[]
}

export interface DetailedOrderTrackingNumber {
    id: number
    status: DetailedOrderTrackingNumberRaw['status']
    number: string
    carrier: DetailedOrderTrackingNumberRaw['carrier']
    serviceLevel: DetailedOrderTrackingNumberRaw['service_level']
    eta: string | null
    transitAt: string | null
    deliveredAt: string | null
    returnedAt: string | null
    failedAt: string | null
    packageIdentifier: string | null
    trackingUrl: string
}

export interface DetailedOrderMessage {
    source: string
    text: string
}

// Carrier rates, parcels and totals stay in their wire form: nothing on the
// detail reads them, and the legacy assignment payload has always sent them raw.
export interface DetailedOrderQuoteItem {
    carrierRate: DetailedOrderQuoteItemRaw['carrier_rate']
    parcels: DetailedOrderQuoteItemRaw['parcels']
}

export interface DetailedOrderQuote {
    id: string
    total: DetailedOrderQuoteRaw['total']
    carrier: DetailedOrderQuoteRaw['carrier']
    serviceLevel: DetailedOrderQuoteRaw['service_level']
    provider: DetailedOrderQuoteRaw['provider']
    deliveryDate?: string
    deliveryDays?: number
    quoteItems: DetailedOrderQuoteItem[]
}

export interface DetailedOrderQuotes {
    selectedQuoteId?: string
    cheapestQuoteId?: string
    fastestQuoteId?: string
    preferenceQuoteIds?: string[]
    quotes: DetailedOrderQuote[]
    messages: DetailedOrderMessage[]
    errors?: DetailedOrderMessage[]
}

export interface DetailedOrder {
    id: number
    createdAt: string | null
    updatedAt: string | null
    customerId: number
    shippingAddressId: number
    billingAddressId: number
    orderId: string
    channelId: number
    channelOrderId: string | null
    orderDate: string
    shipBy: string | null
    arriveBy: string | null
    status: DetailedOrderRaw['status']
    warehouseNotes: string | null
    giftNote: string | null
    shippingMethodPreference: string | null
    pickAssignedTo: number | null
    packAssignedTo: number | null
    quotes: DetailedOrderQuotes | null
    errors: DetailedOrderMessage[] | null
    deletedAt: string | null
    currency: DetailedOrderRaw['currency']
    toteCode: string | null
    shipmentUuid: string
    warehouseUuid: string
    trackingNumbers: DetailedOrderTrackingNumber[]
    channel: DetailedOrderChannel
    orderItems: DetailedOrderItem[]
    customer: DetailedOrderCustomer | null
    shippingAddress: DetailedOrderShippingAddress | null
    packages: DetailedOrderPackage[]
    attachments: DetailedOrderAttachment[]
}

export interface DetailedOrderFulfillment {
    id: string
    createdAt: string
    packageIdentifier: string
    providerConfirmedAt: string | null
}

export interface DetailedOrderFulfillmentRequest {
    id: string
    createdAt: string
    orderId: number
    providerFulfillmentOrderId: string
    receivedAt: string
    isAccepted: boolean
    acceptedAt: string | null
    isRejected: boolean
    rejectedAt: string | null
    fulfillments: DetailedOrderFulfillment[]
}

export interface OrderDetail {
    order: DetailedOrder
    fulfillmentRequests: DetailedOrderFulfillmentRequest[]
}

function toDetailedOrderChannel(raw: DetailedOrderChannelRaw): DetailedOrderChannel {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        type: raw.type,
        nickname: raw.nickname,
        channelAccountId: raw.channel_account_id,
        connectionStatus: raw.connection_status,
        merchantId: raw.merchant_id,
        uuid: raw.uuid,
        merchantUuid: raw.merchant_uuid,
        deletedAt: raw.deleted_at,
        teamId: raw.team_id,
    };
}

function toDetailedOrderCustomer(raw: DetailedOrderCustomerRaw): DetailedOrderCustomer {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        name: raw.name,
        channelId: raw.channel_id,
        defaultAddressId: raw.default_address_id,
        email: raw.email,
        phoneNumber: raw.phone_number,
        emailHash: raw.email_hash,
        nameAddressHash: raw.name_address_hash,
        merchantId: raw.merchant_id,
        teamId: raw.team_id,
    };
}

function toDetailedOrderShippingAddress(raw: DetailedOrderShippingAddressRaw): DetailedOrderShippingAddress {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        customerId: raw.customer_id,
        addressLine1: raw.address_line_1,
        addressLine2: raw.address_line_2,
        addressLine3: raw.address_line_3,
        city: raw.city,
        stateProvince: raw.state_province,
        countryCode: raw.country_code,
        postalCode: raw.postal_code,
        phoneNumber: raw.phone_number,
        isResidential: raw.is_residential,
        name: raw.name,
        latitude: raw.latitude,
        longitude: raw.longitude,
    };
}

function toDetailedOrderItemIdentifier(raw: DetailedOrderItemIdentifierRaw): ItemIdentifier {
    return {
        id: raw.id,
        identifier: raw.identifier,
        identifierType: raw.identifier_type,
    };
}

function toDetailedOrderItemImage(raw: DetailedOrderItemImageRaw): Image {
    return {
        id: raw.id,
        largeUrl: raw.large_url,
        thumbnailUrl: raw.thumbnail_url,
    };
}

function toDetailedOrderInventoryItem(raw: DetailedOrderInventoryItemRaw): DetailedOrderInventoryItem {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        merchantSku: raw.merchant_sku,
        title: raw.title,
        condition: raw.condition,
        conditionNote: raw.condition_note,
        lengthMm: raw.length_mm,
        widthMm: raw.width_mm,
        heightMm: raw.height_mm,
        weightGm: raw.weight_gm,
        uuid: raw.uuid,
        merchantId: raw.merchant_id,
        deletedAt: raw.deleted_at,
        areDimensionsLocked: raw.are_dimensions_locked,
        teamId: raw.team_id,
        searchableIdentifiers: raw.searchableIdentifiers,
        identifiers: raw.identifiers.map(toDetailedOrderItemIdentifier),
        images: raw.images.map(toDetailedOrderItemImage),
    };
}

function toDetailedOrderPickScan(raw: DetailedOrderPickScanRaw): DetailedOrderPickScan {
    if ('code' in raw) {
        return { type: raw.type, code: raw.code, scannedByUserId: raw.scanned_by_user_id, scannedAt: raw.scanned_at };
    }

    return { type: raw.type, skippedByUserId: raw.skipped_by_user_id, skippedAt: raw.skipped_at };
}

function toDetailedOrderPickData(raw: DetailedOrderPickDataRaw): DetailedOrderPickData | [] | null {
    if (raw === null) return null;
    // The server only sends an array for pick data stored empty, so there are no entries to carry over.
    if (Array.isArray(raw)) return [];

    return {
        scans: raw.scans?.map(toDetailedOrderPickScan),
        quantityPicked: raw.quantity_picked,
        toteCode: raw.tote_code,
    };
}

function toDetailedOrderItem(raw: DetailedOrderItemRaw): DetailedOrderItem {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        orderId: raw.order_id,
        itemId: raw.item_id,
        listingId: raw.listing_id,
        quantity: raw.quantity,
        pickedAt: raw.picked_at,
        pickData: toDetailedOrderPickData(raw.pick_data),
        externalId: raw.external_id,
        shipmentLineUuid: raw.shipment_line_uuid,
        item: toDetailedOrderInventoryItem(raw.item),
    };
}

function toDetailedOrderAttachment(raw: DetailedOrderAttachmentRaw): DetailedOrderAttachment {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        path: raw.path,
        name: raw.name,
        url: raw.url,
        attachableId: raw.attachable_id,
        attachableType: raw.attachable_type,
    };
}

function toDetailedOrderPackageBox(raw: DetailedOrderPackageBoxRaw): DetailedOrderPackageBox {
    return {
        id: raw.id,
        packageId: raw.package_id,
        weightGm: raw.weight_gm,
        lengthMm: raw.length_mm,
        widthMm: raw.width_mm,
        heightMm: raw.height_mm,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
    };
}

function toDetailedOrderPackageItem(raw: DetailedOrderPackageItemRaw): DetailedOrderPackageItem {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        orderItemId: raw.order_item_id,
        packageId: raw.package_id,
        quantity: raw.quantity,
        toteBarcode: raw.tote_barcode,
        packedAt: raw.packed_at,
        packedByUserId: raw.packed_by_user_id,
        lotCode: raw.lot_code,
    };
}

function toDetailedOrderPackage(raw: DetailedOrderPackageRaw): DetailedOrderPackage {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        orderId: raw.order_id,
        shippedAt: raw.shipped_at,
        arrivedAt: raw.arrived_at,
        lpn: raw.lpn,
        hash: raw.hash,
        fulfillmentRequestId: raw.fulfillment_request_id,
        box: raw.box === null ? null : toDetailedOrderPackageBox(raw.box),
        attachments: raw.attachments.map(toDetailedOrderAttachment),
        packageItems: raw.package_items.map(toDetailedOrderPackageItem),
    };
}

function toDetailedOrderTrackingNumber(raw: DetailedOrderTrackingNumberRaw): DetailedOrderTrackingNumber {
    return {
        id: raw.id,
        status: raw.status,
        number: raw.number,
        carrier: raw.carrier,
        serviceLevel: raw.service_level,
        eta: raw.eta,
        transitAt: raw.transit_at,
        deliveredAt: raw.delivered_at,
        returnedAt: raw.returned_at,
        failedAt: raw.failed_at,
        packageIdentifier: raw.package_identifier,
        trackingUrl: raw.tracking_url,
    };
}

function toDetailedOrderMessage(raw: DetailedOrderMessageRaw): DetailedOrderMessage {
    return {
        source: raw.source,
        text: raw.text,
    };
}

function toDetailedOrderQuoteItem(raw: DetailedOrderQuoteItemRaw): DetailedOrderQuoteItem {
    return {
        carrierRate: raw.carrier_rate,
        parcels: raw.parcels,
    };
}

function toDetailedOrderQuote(raw: DetailedOrderQuoteRaw): DetailedOrderQuote {
    return {
        id: raw.id,
        total: raw.total,
        carrier: raw.carrier,
        serviceLevel: raw.service_level,
        provider: raw.provider,
        deliveryDate: raw.delivery_date,
        deliveryDays: raw.delivery_days,
        quoteItems: raw.quote_items.map(toDetailedOrderQuoteItem),
    };
}

function toDetailedOrderQuotes(raw: DetailedOrderQuotesRaw): DetailedOrderQuotes {
    return {
        selectedQuoteId: raw.selected_quote_id,
        cheapestQuoteId: raw.cheapest_quote_id,
        fastestQuoteId: raw.fastest_quote_id,
        preferenceQuoteIds: raw.preference_quote_ids,
        quotes: raw.quotes.map(toDetailedOrderQuote),
        messages: raw.messages.map(toDetailedOrderMessage),
        errors: raw.errors?.map(toDetailedOrderMessage),
    };
}

function toDetailedOrder(raw: DetailedOrderRaw): DetailedOrder {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        updatedAt: raw.updated_at,
        customerId: raw.customer_id,
        shippingAddressId: raw.shipping_address_id,
        billingAddressId: raw.billing_address_id,
        orderId: raw.order_id,
        channelId: raw.channel_id,
        channelOrderId: raw.channel_order_id,
        orderDate: raw.order_date,
        shipBy: raw.ship_by,
        arriveBy: raw.arrive_by,
        status: raw.status,
        warehouseNotes: raw.warehouse_notes,
        giftNote: raw.gift_note,
        shippingMethodPreference: raw.shipping_method_preference,
        pickAssignedTo: raw.pick_assigned_to,
        packAssignedTo: raw.pack_assigned_to,
        quotes: raw.quotes === null ? null : toDetailedOrderQuotes(raw.quotes),
        errors: raw.errors === null ? null : raw.errors.map(toDetailedOrderMessage),
        deletedAt: raw.deleted_at,
        currency: raw.currency,
        toteCode: raw.tote_code,
        shipmentUuid: raw.shipment_uuid,
        warehouseUuid: raw.warehouse_uuid,
        trackingNumbers: raw.tracking_numbers.map(toDetailedOrderTrackingNumber),
        channel: toDetailedOrderChannel(raw.channel),
        orderItems: raw.order_items.map(toDetailedOrderItem),
        customer: raw.customer === null ? null : toDetailedOrderCustomer(raw.customer),
        shippingAddress: raw.shipping_address === null ? null : toDetailedOrderShippingAddress(raw.shipping_address),
        packages: raw.packages.map(toDetailedOrderPackage),
        attachments: raw.attachments.map(toDetailedOrderAttachment),
    };
}

function toDetailedOrderFulfillment(raw: DetailedOrderFulfillmentRaw): DetailedOrderFulfillment {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        packageIdentifier: raw.package_identifier,
        providerConfirmedAt: raw.provider_confirmed_at,
    };
}

function toDetailedOrderFulfillmentRequest(raw: DetailedOrderFulfillmentRequestRaw): DetailedOrderFulfillmentRequest {
    return {
        id: raw.id,
        createdAt: raw.created_at,
        orderId: raw.order_id,
        providerFulfillmentOrderId: raw.provider_fulfillment_order_id,
        receivedAt: raw.received_at,
        isAccepted: raw.is_accepted,
        acceptedAt: raw.accepted_at,
        isRejected: raw.is_rejected,
        rejectedAt: raw.rejected_at,
        fulfillments: raw.fulfillments.map(toDetailedOrderFulfillment),
    };
}

export function detailedOrderPickDataToteCode(pickData: DetailedOrderItem['pickData']): string | null | undefined {
    return pickData === null || Array.isArray(pickData) ? undefined : pickData.toteCode;
}

export function toOrderDetail(raw: GetOrderResponse): OrderDetail {
    return {
        order: toDetailedOrder(raw.order),
        fulfillmentRequests: raw.fulfillment_requests.map(toDetailedOrderFulfillmentRequest),
    };
}
